<template>
    <div class="sequenceOptions">
        <v-row>
            <v-col>
                <v-text-field v-model="height" type="number" label="Control height" @input="emit" @change="emit"/>
                <span class="body-2 text--secondary">The overall height of the control. For example 700, 700px, 50%.</span>
            </v-col>
            <v-col>
                <v-select :items="[{text: 'US', value: 'US'}, {text: 'Europe', value: 'Europe'}]" v-model="teamOrder" label="Team order" @change="emit"/>
                <span class="body-2 text--secondary">The order in which to display the teams. US = Away then Home, Europe = Home then Away.</span>
            </v-col>         
            <v-col>
                <v-text-field v-model="nameFormat" type="string" label="Name format" @input="emit" @change="emit"/>
                <span class="body-2 text--secondary">The set of player fields to use for the player in the events list. Each field should be enclosed in {}. Spaces are significant.<br> Example {jersey} {firstName} {lastName}.</span>
            </v-col>
            <v-col>
                <v-text-field v-model="actionsColumns" type="number" label="Number of action columns" @input="emit" @change="emit"/>
                <span class="body-2 text--secondary">The number of columns in which to display actions buttons.</span>            
            </v-col>
            <v-col>
                <v-text-field v-model="eventsHeight" type="number" label="Events height" @input="emit" @change="emit"/>
                <span class="body-2 text--secondary">The height of the events table in the control.</span>
            </v-col>
        </v-row>
        <v-row>
            <v-col>
                <v-text-field v-model="playerHeight" type="number" label="Players height" @input="emit" @change="emit"/>
                <span class="body-2 text--secondary">The height of the players' tables in the control.</span>
            </v-col>
            <v-col>
                <v-text-field v-model="playerResultSet" type="string" label="Player result set" @input="emit" @change="emit"/>
                <span class="body-2 text--secondary">The name of the result set to use. Default uses the first set returned.</span>
            </v-col>
            <v-col>
                <v-text-field v-model="playerItemId" type="string" label="Player item id" @input="emit" @change="emit"/>
                <span class="body-2 text--secondary">The name of the player property to use as the items Id. Default is 'Id'</span>
            </v-col>
            <v-col>
                <v-text-field v-model="playerSortField" type="string" label="Player sort field" @input="emit" @change="emit"/>
                <span class="body-2 text--secondary">The field by which the players should be initially sorted.</span>
            </v-col>
            <v-col>
                <v-select :items="[{text: 'Ascending', value: 'asc'}, {text: 'Descending', value: 'desc'}]" v-model="playerSortDirection" label="Player sort direction" @change="emit"/>
                <span class="body-2 text--secondary">How to sort the players. Default is Ascending.</span>
            </v-col>         
        </v-row>
        <div class="columnsWrapper">
            <label class="v-label theme--dark">Player Column Definitions</label>
            <v-row class="entryRow">
                <v-col>
                    <v-text-field v-model="playerColumnField" type="string" label="Field"/>
                    <span class="body-2 text--secondary">The field for this column.</span>
                </v-col>
                <v-col>
                    <v-text-field v-model="playerColumnDisplayName" type="string" label="Display name"/>
                    <span class="body-2 text--secondary">The display name for this column.</span>
                </v-col>
                <v-col>
                    <v-text-field v-model="playerColumnWidth" type="string" label="Width"/>
                    <span class="body-2 text--secondary">The width in pixels for this column.</span>
                </v-col>
                <v-col>
                    <v-btn dark @click="save">Save Column Definition</v-btn>
                </v-col>
            </v-row>

            <v-row class="headerRow">
                <div class="columnField">Field</div>
                <div class="columnField">Display Name</div>
                <div class="columnField">Width (optional)</div>
                <div class="columnField">Delete Column Definition</div>
            </v-row>
            <div class="columnDefs">
                <v-row v-for="(col, idx) in playerColumns" v-bind:key="col.field" @click="selectColumn(col)" :class="selectedIdx === idx ? 'selected' : null">
                    <div class="columnField text--secondary">
                        {{col.field}}
                    </div>
                    <div class="columnField text--secondary">
                        {{col.displayName}}
                    </div>
                    <div class="columnField text--secondary">
                        {{col.width}}
                    </div>
                    <div class="columnField text--secondary">
                        <v-icon @click.stop="remove(col)" color="red">mdi-close</v-icon>
                    </div>
                </v-row>
            </div>
        </div>
    </div>
</template>

<script>
    export default {
        name: 'Sequence',
        data: () => ({
            loadLocal: false,
            height: null,
            teamOrder: 'US',
            nameFormat: null,
            playerSortField: null,
            playerSortDirection: 'asc',
            playerColumns: [],
            playerColumnField: null,
            playerColumnDisplayName: null,
            playerColumnWidth: null,
            playerHeight: null,
            playerResultSet: null,
            playerItemId: null,
            actionsColumns: 3,
            eventsHeight: 200,
            selectedIdx: 0
        }),
        computed: {
        },
        methods: {
            emit() {
                let opts = {
                    type: 'sequence',
                    loadLocal: this.loadLocal};

                if (this.height){
                    let reg = new RegExp('^\\d*$');
                    if (reg.test(this.height)){
                        // Just numeric
                        opts.height = parseInt(this.height);
                    } else {
                        // Just treat as a string - no attempt to check validity
                        opts.height = this.height;
                    }
                }

                opts.teamOrder = this.teamOrder;

                if(this.nameFormat){
                    opts.nameFormat = this.nameFormat;
                }

                opts.actions = this.buildActionOptions();
                opts.events = this.buildEventOptions();
                opts.players = this.buildPlayerOptions();

                this.$emit('change', opts);
            },
            buildPlayerOptions(){
                let players = {};

                if (this.playerHeight){
                    players.height = this.playerHeight;
                }

                if (this.playerResultSet){
                    players.resultSet = this.playerResultSet;
                }

                if (this.playerItemId){
                    players.itemId = this.playerItemId;
                }

                if (this.playerSortField){
                    let sort = {field: this.playerSortField, direction: this.playerSortDirection};
                    players.sort = sort;
                }

                if (this.playerColumns.length > 0){
                    let colDefs = [];                    
                    this.playerColumns.forEach(col => {
                        if (col.field != null){
                            let entry = {field: col.field, displayName: col.displayName};
                            if (col.width){
                                entry.width = col.width;
                            }
                            colDefs.push(entry);
                        }
                    });
                    players.columns = colDefs;
                }

                return players;
            },
            buildActionOptions(){
                return {numberColumns: this.actionsColumns};
            },
            buildEventOptions(){
                return {height: this.eventsHeight};
            },
            addNew(){
                // Clear the data and increase selected row index
                this.selectedIdx = this.playerColumns.length;
                this.playerColumnField = null;
                this.playerColumnDisplayName = null;
                this.playerColumnWidth = null;
            },
            save(){
                // Save the data to the current row
                if (this.playerColumnField != null){
                    this.playerColumns[this.selectedIdx] = {field: this.playerColumnField, displayName: this.playerColumnDisplayName, width: this.playerColumnWidth};
                    this.addNew();
                    this.emit();                
                }
            },
            selectColumn(col){
                let idx = this.playerColumns.findIndex(c => c.field === col.field);
                this.selectedIdx = idx;
                this.playerColumnField = col.field;
                this.playerColumnDisplayName = col.displayName;
                this.playerColumnWidth = col.width;
            },
            remove(col){
                let idx = this.playerColumns.findIndex(c => c.field === col.field);
                this.playerColumns.splice(idx, 1);
                this.emit();
            }

        },
        created() {
            this.emit();
        }

    }
</script>

<style lang="scss" scoped>
    .columnsWrapper {
    
        .selected {
            border: 1px solid rgba($color: white, $alpha: 0.5);
        }
        
        .columnField {
            margin: 0 15px;
            display: flex;
            flex: 1 1 0;
        }
    }

</style>